import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { classificationService } from '../../services/classificationService';
import SubcategoryForm from '../../components/Classification/SubcategoryForm';
import ClassificationExcelToolbar from '../../components/Classification/ClassificationExcelToolbar';
import {
  ClassificationBulkDeleteModal,
  ClassificationBulkDeleteTd,
  ClassificationBulkDeleteTh,
  ClassificationBulkDeleteToolbar,
} from '../../components/Classification/ClassificationBulkDeleteUi';
import ConfirmModal from '../../components/Common/ConfirmModal';
import Toast from '../../components/Common/Toast';
import MutateGate from '../../components/Common/MutateGate';
import { useCanMutate } from '../../hooks/useCanMutate';
import { useClassificationBulkDelete } from '../../hooks/useClassificationBulkDelete';
import { useClassificationHierarchyForBulk } from '../../hooks/useClassificationHierarchyForBulk';

const Subcategories = () => {
  const canMutate = useCanMutate();
  const [searchParams] = useSearchParams();
  const categoryId = searchParams.get('category');
  
  const [subcategories, setSubcategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(categoryId || '');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [subcategoryToDelete, setSubcategoryToDelete] = useState(null);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const hasLoaded = useRef(false);
  const previousCategory = useRef(selectedCategory);
  const { hierarchy: fullHierarchy, reloadHierarchy } = useClassificationHierarchyForBulk(
    classificationService,
    subcategories.length
  );

  useEffect(() => {
    // Evitar doble carga en React StrictMode
    if (!hasLoaded.current) {
      hasLoaded.current = true;
      loadData();
      previousCategory.current = selectedCategory;
    }
  }, []);

  useEffect(() => {
    // Recargar cuando cambie el filtro de categoría
    // Solo si ya se cargó inicialmente Y el valor realmente cambió
    if (hasLoaded.current && previousCategory.current !== selectedCategory) {
      previousCategory.current = selectedCategory;
      loadSubcategories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory]);

  const loadData = async () => {
    await Promise.all([loadCategories(), loadSubcategories()]);
  };

  const loadCategories = async () => {
    try {
      console.log('📁 [Subcategories] Cargando categorías...');
      const response = await classificationService.getCategories({ active_only: false });
      
      if (response.success) {
        setCategories(response.data);
        console.log('✅ [Subcategories] Categorías cargadas:', response.data.length);
      }
    } catch (error) {
      console.error('❌ [Subcategories] Error al cargar categorías:', error);
    }
  };

  const loadSubcategories = async () => {
    try {
      setLoading(true);
      console.log('📁 [Subcategories] Cargando subcategorías...');
      console.log('🌐 [Subcategories] Filtro categoría:', selectedCategory || 'Todas');
      
      const params = {};
      if (selectedCategory) {
        params.category_id = selectedCategory;
      }
      
      const response = await classificationService.getSubcategories(params);
      
      console.log('✅ [Subcategories] Subcategorías cargadas exitosamente');
      console.log('📊 [Subcategories] Respuesta:', response);
      console.log('📦 [Subcategories] Total de subcategorías:', response.data?.length || 0);
      
      if (response.success) {
        setSubcategories(response.data);
        console.log('💾 [Subcategories] Subcategorías guardadas en estado');
      } else {
        console.warn('⚠️ [Subcategories] Respuesta sin éxito:', response);
      }
    } catch (error) {
      console.error('❌ [Subcategories] Error al cargar subcategorías:', error);
      console.error('📊 [Subcategories] Respuesta del servidor:', error.response?.data);
    } finally {
      setLoading(false);
      console.log('🏁 [Subcategories] Proceso de carga finalizado');
    }
  };

  const handleDeleteClick = (subcategory) => {
    setSubcategoryToDelete(subcategory);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!subcategoryToDelete) return;

    try {
      console.log('🗑️ [Subcategories] Eliminando subcategoría:', subcategoryToDelete.id);
      const response = await classificationService.deleteSubcategory(subcategoryToDelete.id);
      
      if (response.success) {
        console.log('✅ [Subcategories] Subcategoría eliminada exitosamente');
        setIsDeleteModalOpen(false);
        setSubcategoryToDelete(null);
        setToast({
          isOpen: true,
          message: 'Subcategoría eliminada exitosamente',
          type: 'success'
        });
        loadSubcategories(); // Recargar la lista
      } else {
        console.error('❌ [Subcategories] Error al eliminar:', response.message);
        setIsDeleteModalOpen(false);
        setToast({
          isOpen: true,
          message: response.message || 'Error al eliminar la subcategoría',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('❌ [Subcategories] Error al eliminar:', error);
      setIsDeleteModalOpen(false);
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al eliminar la subcategoría',
        type: 'error'
      });
    }
  };

  const handleFormSuccess = () => {
    console.log('✅ [Subcategories] Formulario guardado exitosamente, recargando lista...');
    loadSubcategories();
  };

  const filteredSubcategories = subcategories.filter(sub => {
    const matchesSearch = sub.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sub.code.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const bulk = useClassificationBulkDelete({
    variant: 'subcategories',
    rows: filteredSubcategories,
    hierarchy: fullHierarchy,
    blockRecetaOrigin: true,
    deleteItem: (id) => classificationService.deleteSubcategory(id),
    onDeleted: async () => {
      await loadSubcategories();
      await reloadHierarchy();
    },
    setToast,
    enabled: canMutate,
  });

  const selectedCategoryName = selectedCategory 
    ? categories.find(c => c.id === parseInt(selectedCategory))?.name 
    : 'Todas';

  const selectedCategoryHasRecetaOrigin = useMemo(() => {
    if (!selectedCategory) return false;
    const c = categories.find((x) => String(x.id) === String(selectedCategory));
    return Boolean(c?.receta_category_id);
  }, [categories, selectedCategory]);

  const isFromRecetasSub = (row) =>
    row?.receta_subcategory_id != null && row.receta_subcategory_id !== '';

  // Mostrar loading solo cuando está cargando por primera vez
  if (loading && !hasLoaded.current) {
    return (
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-center items-center h-64">
            <div className="text-lg text-gray-600">Cargando subcategorías...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:justify-between lg:items-start mb-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-800">Subcategorías</h1>
                <p className="text-gray-600 mt-1">Nivel 2 - Clasificación de Productos</p>
                {selectedCategory && (
                  <p className="text-sm text-teal-600 mt-1">
                    Categoría: <span className="font-medium">{selectedCategoryName}</span>
                  </p>
                )}
              </div>
              <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
                <ClassificationExcelToolbar
                  variant="subcategories"
                  onImported={loadSubcategories}
                  setToast={setToast}
                  exportRows={filteredSubcategories}
                  exportContext={{ categories }}
                />
                <MutateGate>
                  <button
                    type="button"
                    disabled={selectedCategoryHasRecetaOrigin}
                    title={
                      selectedCategoryHasRecetaOrigin
                        ? 'Las subcategorías de esta categoría se gestionan desde Recetas'
                        : undefined
                    }
                    onClick={() => {
                      setEditingSubcategory(null);
                      setIsFormOpen(true);
                    }}
                    className={`px-4 py-2 rounded-md font-medium transition-colors text-white ${
                      selectedCategoryHasRecetaOrigin
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-teal-600 hover:bg-teal-700'
                    }`}
                  >
                    + Nueva Subcategoría
                  </button>
                </MutateGate>
              </div>
            </div>

            {/* Breadcrumb */}
            <nav className="text-sm text-gray-600 mb-4">
              <Link to="/dashboard" className="hover:text-teal-600">Dashboard</Link>
              <span className="mx-2">/</span>
              <Link to="/classification" className="hover:text-teal-600">Clasificación</Link>
              <span className="mx-2">/</span>
              <span className="text-gray-800">Subcategorías</span>
            </nav>

            {/* Filtros en una sola línea */}
            <div className="flex flex-nowrap items-end gap-3 mb-4 w-full overflow-x-auto pb-0.5">
              <div className="flex flex-col gap-1 flex-shrink-0 w-44 sm:w-56">
                <label htmlFor="subcategories-filter-category" className="text-xs font-medium text-gray-600 whitespace-nowrap">
                  Categoría
                </label>
                <select
                  id="subcategories-filter-category"
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                >
                  <option value="">Todas las categorías</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.code} — {cat.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-1 flex-1 min-w-[12rem] basis-0 min-h-0">
                <label htmlFor="subcategories-search" className="text-xs font-medium text-gray-600 whitespace-nowrap">
                  Buscar
                </label>
                <input
                  id="subcategories-search"
                  type="text"
                  placeholder="Buscar subcategorías..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full min-w-[12rem] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                />
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg shadow-md p-4">
              <div className="text-sm text-gray-600">Total Subcategorías</div>
              <div className="text-2xl font-bold text-gray-800">{filteredSubcategories.length}</div>
            </div>
            <div className="bg-white rounded-lg shadow-md p-4">
              <div className="text-sm text-gray-600">Subcategorías Activas</div>
              <div className="text-2xl font-bold text-green-600">
                {filteredSubcategories.filter(s => s.is_active).length}
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-md p-4">
              <div className="text-sm text-gray-600">Subcategorías Inactivas</div>
              <div className="text-2xl font-bold text-red-600">
                {filteredSubcategories.filter(s => !s.is_active).length}
              </div>
            </div>
          </div>

          {/* Tabla: scroll horizontal + acciones fijas; texto largo con wrap */}
          <ClassificationBulkDeleteToolbar bulk={bulk} />

          <div className="bg-white rounded-lg shadow-md overflow-x-auto">
            {filteredSubcategories.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                {searchTerm ? 'No se encontraron subcategorías con ese criterio' : 'No hay subcategorías registradas'}
              </div>
            ) : (
              <table className="w-full min-w-[min(100%,48rem)] divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <ClassificationBulkDeleteTh bulk={bulk} />
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Código
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider max-w-[11rem]">
                      Nombre
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider max-w-[14rem]">
                      Categoría
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider max-w-[12rem]">
                      Descripción
                    </th>
                    <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Estado
                    </th>
                    <th className="sticky right-0 z-20 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 border-l border-gray-200 shadow-[-8px_0_12px_-6px_rgba(0,0,0,0.12)] whitespace-nowrap">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredSubcategories.map((subcategory) => (
                    <tr key={subcategory.id} className="group hover:bg-gray-50">
                      <ClassificationBulkDeleteTd bulk={bulk} row={subcategory} />
                      <td className="px-3 py-3 align-top whitespace-nowrap">
                        <span className="text-sm font-medium text-gray-900">{subcategory.code}</span>
                      </td>
                      <td className="px-3 py-3 align-top max-w-[11rem] break-words">
                        <span className="text-sm text-gray-900 inline-flex items-center gap-2 flex-wrap">
                          {subcategory.name}
                          {isFromRecetasSub(subcategory) ? (
                            <span
                              className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 border border-amber-200"
                              title="Sincronizada desde recetas; no se puede eliminar aquí"
                            >
                              Recetas
                            </span>
                          ) : null}
                        </span>
                      </td>
                      <td className="px-3 py-3 align-top max-w-[14rem] break-words">
                        <Link
                          to={`/classification/categories`}
                          className="text-sm text-teal-600 hover:text-teal-900 break-words"
                        >
                          {subcategory.category?.code || ''} - {subcategory.category?.name || 'N/A'}
                        </Link>
                      </td>
                      <td className="px-3 py-3 align-top max-w-[12rem] break-words">
                        <span className="text-sm text-gray-500">{subcategory.description || '-'}</span>
                      </td>
                      <td className="px-2 py-3 align-top whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                          subcategory.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {subcategory.is_active ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td className="sticky right-0 z-10 px-3 py-3 align-top text-sm font-medium bg-white border-l border-gray-200 shadow-[-8px_0_12px_-6px_rgba(0,0,0,0.12)] group-hover:bg-gray-50">
                        <div className="flex flex-col gap-1 whitespace-nowrap">
                          <Link
                            to={`/classification/families?subcategory=${subcategory.id}`}
                            className="text-teal-600 hover:text-teal-900"
                          >
                            Ver familias
                          </Link>
                          {canMutate ? (
                            <>
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingSubcategory(subcategory);
                                  setIsFormOpen(true);
                                }}
                                className="text-purple-600 hover:text-purple-900 text-left"
                              >
                                Editar
                              </button>
                              {isFromRecetasSub(subcategory) ? (
                                <span
                                  className="text-xs text-gray-500 text-left"
                                  title="Elimine o gestione esta subcategoría desde Producción → Clasificación de recetas"
                                >
                                  Sin eliminar (recetas)
                                </span>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => handleDeleteClick(subcategory)}
                                  className="text-red-600 hover:text-red-900 text-left"
                                >
                                  Eliminar
                                </button>
                              )}
                            </>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Modal de Formulario */}
        <SubcategoryForm
          isOpen={isFormOpen}
          onClose={() => {
            setIsFormOpen(false);
            setEditingSubcategory(null);
          }}
          subcategory={editingSubcategory}
          onSuccess={handleFormSuccess}
        />

        <ClassificationBulkDeleteModal bulk={bulk} />

        {/* Modal de Confirmación de Eliminación */}
        <ConfirmModal
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setSubcategoryToDelete(null);
          }}
          onConfirm={handleDeleteConfirm}
          title="Eliminar Subcategoría"
          message={`¿Estás seguro de que deseas eliminar la subcategoría "${subcategoryToDelete?.name}" (${subcategoryToDelete?.code})? Esta acción no se puede deshacer.`}
          confirmText="Eliminar"
          cancelText="Cancelar"
          type="danger"
        />

        {/* Toast de notificaciones */}
        <Toast
          isOpen={toast.isOpen}
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, isOpen: false })}
        />
      </div>
    </div>
  );
};

export default Subcategories;
